/*
 * Copyright 2025 MadeByTatwa LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { existsSync, readdirSync, statSync } from 'fs';
import { join } from 'path';
import type { DetectorResult } from '../types/index.js';
import { createDetectorResult, type Detector } from './base.js';
import { readJsonFile } from '../utils/fs.js';

/**
 * Monorepo Detector
 *
 * Detects monorepo setups (Nx, Turborepo, PNPM, Yarn/npm workspaces, Lerna)
 * and provides guidance on running projects within the monorepo.
 */
export class MonorepoDetector implements Detector {
  name = 'monorepo';

  async detect(cwd: string): Promise<DetectorResult> {
    const reasons: string[] = [];
    let confidence = 0;
    let monorepoType: string | null = null;
    let cmd = '';
    let args: string[] = [];

    // Check for Nx
    if (existsSync(join(cwd, 'nx.json'))) {
      monorepoType = 'Nx';
      reasons.push('Found nx.json (Nx workspace)');
      confidence += 40;

      const apps = findNxApps(cwd);
      if (apps.length > 0) {
        reasons.push(`Found ${apps.length} app(s): ${apps.slice(0, 3).join(', ')}${apps.length > 3 ? '...' : ''}`);
        confidence += 20;

        // Default to first app
        cmd = 'nx';
        args = ['serve', apps[0]];
        reasons.push(`Default target: ${apps[0]}`);
      } else {
        cmd = 'nx';
        args = ['run-many', '--target=serve', '--all'];
        reasons.push('No specific app found, using run-many');
      }
      confidence += 10;
    }

    // Check for Turborepo
    if (existsSync(join(cwd, 'turbo.json'))) {
      monorepoType = 'Turborepo';
      reasons.push('Found turbo.json (Turborepo workspace)');
      confidence += 40;

      const apps = findTurborepoApps(cwd);
      if (apps.length > 0) {
        reasons.push(`Found ${apps.length} app(s): ${apps.slice(0, 3).join(', ')}${apps.length > 3 ? '...' : ''}`);
        confidence += 20;
      }

      cmd = 'turbo';
      args = ['run', 'dev'];
      reasons.push('Running all dev tasks');
      confidence += 10;
    }

    // Check for PNPM workspaces
    if (existsSync(join(cwd, 'pnpm-workspace.yaml'))) {
      if (!monorepoType) {
        monorepoType = 'PNPM Workspace';
        reasons.push('Found pnpm-workspace.yaml');
        confidence += 35;

        const packages = findWorkspacePackages(cwd);
        if (packages.length > 0) {
          reasons.push(`Found ${packages.length} package(s)`);
          confidence += 15;
        }

        cmd = 'pnpm';
        args = ['run', '-r', 'dev'];
        reasons.push('Running dev script in all packages');
        confidence += 10;
      }
    }

    // Check for Yarn/npm workspaces in package.json
    const packageJsonPath = join(cwd, 'package.json');
    if (existsSync(packageJsonPath)) {
      const pkg = (await readJsonFile(packageJsonPath)) as { workspaces?: string[] | { packages: string[] } };

      if (pkg?.workspaces) {
        if (!monorepoType) {
          const pm = existsSync(join(cwd, 'yarn.lock'))
            ? 'Yarn'
            : existsSync(join(cwd, 'pnpm-lock.yaml'))
              ? 'PNPM'
              : 'npm';

          monorepoType = `${pm} Workspace`;
          reasons.push(`Found workspaces in package.json (${pm})`);
          confidence += 30;

          const packages = findWorkspacePackages(cwd);
          if (packages.length > 0) {
            reasons.push(`Found ${packages.length} workspace package(s)`);
            confidence += 15;
          }

          // Determine command based on package manager
          if (pm === 'Yarn') {
            cmd = 'yarn';
            args = ['workspaces', 'foreach', 'run', 'dev'];
          } else if (pm === 'PNPM') {
            cmd = 'pnpm';
            args = ['run', '-r', 'dev'];
          } else {
            cmd = 'npm';
            args = ['run', 'dev', '--workspaces'];
          }

          reasons.push('Running dev script across workspaces');
          confidence += 5;
        }
      }
    }

    // Check for Lerna
    if (existsSync(join(cwd, 'lerna.json'))) {
      if (!monorepoType) {
        monorepoType = 'Lerna';
        reasons.push('Found lerna.json (Lerna monorepo)');
        confidence += 30;

        const packages = findWorkspacePackages(cwd);
        if (packages.length > 0) {
          reasons.push(`Found ${packages.length} package(s)`);
          confidence += 15;
        }

        cmd = 'lerna';
        args = ['run', 'dev'];
        reasons.push('Running dev script via Lerna');
        confidence += 10;
      }
    }

    // If no monorepo detected
    if (!monorepoType) {
      return createDetectorResult(this.name, 0);
    }

    return createDetectorResult(this.name, confidence, {
      cwd,
      pm: null, // Monorepos handle their own PM
      cmd,
      args,
      reasons: [
        ...reasons,
        '💡 Tip: Use --select to choose a specific app/package',
      ],
      detectedType: monorepoType,
    });
  }
}

/**
 * Find Nx apps in the workspace
 */
function findNxApps(cwd: string): string[] {
  try {
    const appsDir = join(cwd, 'apps');
    if (!existsSync(appsDir)) return [];

    return readdirSync(appsDir).filter((name) => {
      const stat = statSync(join(appsDir, name));
      return stat.isDirectory();
    });
  } catch {
    return [];
  }
}

/**
 * Find Turborepo apps (usually in apps/ or packages/)
 */
function findTurborepoApps(cwd: string): string[] {
  try {
    const apps: string[] = [];
    const appsDir = join(cwd, 'apps');
    const packagesDir = join(cwd, 'packages');

    if (existsSync(appsDir)) {
      const appNames = readdirSync(appsDir).filter((name) => {
        const stat = statSync(join(appsDir, name));
        return stat.isDirectory();
      });
      apps.push(...appNames.map((name) => `apps/${name}`));
    }

    if (existsSync(packagesDir)) {
      const pkgNames = readdirSync(packagesDir).filter((name) => {
        const stat = statSync(join(packagesDir, name));
        return stat.isDirectory();
      });
      apps.push(...pkgNames.map((name) => `packages/${name}`));
    }

    return apps;
  } catch {
    return [];
  }
}

/**
 * Find workspace packages (generic for any monorepo type)
 */
function findWorkspacePackages(cwd: string): string[] {
  try {
    const packages: string[] = [];
    const possibleDirs = ['apps', 'packages', 'libs', 'services'];

    for (const dir of possibleDirs) {
      const dirPath = join(cwd, dir);
      if (existsSync(dirPath)) {
        const items = readdirSync(dirPath).filter((name) => {
          const stat = statSync(join(dirPath, name));
          const hasPackageJson = existsSync(join(dirPath, name, 'package.json'));
          return stat.isDirectory() && hasPackageJson;
        });
        packages.push(...items);
      }
    }

    return packages;
  } catch {
    return [];
  }
}

